import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Lock, User, Building2, ArrowRight, Sparkles, Eye, EyeOff, CheckCircle, Star, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useTranslation } from '../hooks/useTranslation';
import { useLinkLanguage } from '../hooks/useLinkLanguage';
import { authErrorMessage } from '../utils/authErrors';
import { redirectToGoogleAuth } from '../utils/googleAuth';
import LanguageSwitcher from './LanguageSwitcher';
import TrustBar from './TrustBar';
import { track as analyticsTrack, identify as analyticsIdentify, EVENTS } from '../utils/analytics';
import { DEMO_FLAGS, sessionFlag } from './demo/demoData';
import { journey, JOURNEY } from '../utils/journey';
import { useRegion } from '../contexts/RegionContext';
import '../styles/premium-auth.css';

const API = process.env.REACT_APP_BACKEND_URL;

// Plan IDs that are valid for direct post-signup Stripe checkout
const VALID_PLAN_IDS = ['starter', 'growth', 'pro'];

// iter-122 — pré-remplissage avec ce que le visiteur a RÉELLEMENT saisi (Scorecard → rapport complet).
const initialFormData = () => {
  const lead = journey.get(JOURNEY.lead) || {};
  const prefillEmail = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('prefill_email') : '';
  return { email: prefillEmail || lead.email || '', password: '', name: lead.name || '', company: lead.restaurant_name || '' };
};

const PremiumRegister = () => {
  const { t, tp, lang } = useTranslation();
  useLinkLanguage();
  const [formData, setFormData] = useState(initialFormData);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Plan + billing pre-selected from /pricing CTA → /register?plan=starter&billing=monthly
  const presetPlan = (searchParams.get('plan') || '').toLowerCase();
  const presetBilling = (searchParams.get('billing') || 'monthly').toLowerCase();
  const presetPromo = (searchParams.get('promo') || '').toUpperCase();
  const hasCheckoutIntent = VALID_PLAN_IDS.includes(presetPlan);
  const isFounding = presetPromo === 'FOUNDING';

  // Check if already logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      navigate('/dashboard');
      return;
    }
    // iter-102 LOT1: measure form abandonment (view vs submit)
    analyticsTrack(EVENTS.REGISTER_STARTED, { stage: 'form_viewed', has_checkout_intent: !!hasCheckoutIntent });
  }, [navigate]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = t('rApp.register.errors.nameRequired');
    if (!formData.email) newErrors.email = t('rApp.register.errors.emailRequired');
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = t('rApp.register.errors.emailInvalid');
    if (!formData.password) newErrors.password = t('rApp.register.errors.passwordRequired');
    else if (formData.password.length < 8) newErrors.password = t('rApp.register.errors.passwordTooShort');
    else if (!/[a-zA-Z]/.test(formData.password) || !/[0-9\W]/.test(formData.password)) {
      newErrors.password = t('rApp.register.errors.passwordWeak');
    }
    if (!acceptedTerms) newErrors.terms = t('rApp.register.errors.termsRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const startStripeCheckout = async (token) => {
    /** Post-signup checkout: hand the user straight to Stripe so they can start their trial. */
    try {
      const successUrl = `${window.location.origin}/subscription/success?session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${window.location.origin}/pricing`;
      const resp = await axios.post(
        `${API}/api/revenue-engine/billing/checkout`,
        {
          plan_id: presetPlan,
          billing_period: presetBilling,
          promo_code: presetPromo || undefined,
          success_url: successUrl,
          cancel_url: cancelUrl,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const url = resp.data?.url || resp.data?.checkout_url || resp.data?.session_url;
      if (url) {
        // Hard redirect to Stripe-hosted checkout
        window.location.href = url;
        return true;
      }
      toast.error(t('rApp.register.errors.checkoutGeneric'));
      return false;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Iter 95 — blocage devise (EUR non configuré) : message localisé du backend
      toast.error(
        typeof detail === 'string'
          ? detail
          : (detail?.message || t('rApp.register.errors.checkoutFailed'))
      );
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    // iter-82: emit register_started event
    analyticsTrack(EVENTS.REGISTER_STARTED, { stage: 'submitted', has_checkout_intent: !!hasCheckoutIntent });

    setLoading(true);
    try {
      // iter-71: forward affiliate referral code if present (query param OR cookie)
      const refFromUrl = searchParams.get('ref');
      const refFromCookie = document.cookie.split(';').map(c => c.trim()).find(c => c.startsWith('cb_ref='));
      const refValue = refFromUrl || (refFromCookie ? decodeURIComponent(refFromCookie.split('=')[1]) : null);
      const payload = {
        ...(refValue ? { ...formData, ref: refValue } : formData),
        accepted_terms: acceptedTerms,
        marketing_opt_in: marketingOptIn,
        locale: lang,
        acceptance_source: hasCheckoutIntent ? 'register_checkout_intent' : 'register',
      };
      const response = await axios.post(`${API}/api/auth/register`, payload);
      const token = response.data.access_token;
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // iter-82: link the anonymous session to the newly created user
      analyticsIdentify('', formData.email);

      // iter-120 — inscription attribuée à la démo interactive (funnel démo)
      if (sessionFlag.get(DEMO_FLAGS.origin)) {
        analyticsTrack(EVENTS.DEMO_SIGNUP_COMPLETED, { has_checkout_intent: !!hasCheckoutIntent });
        sessionFlag.clear(DEMO_FLAGS.origin);
      }

      // iter-122 — le nom du restaurant saisi ici sert au setup Discovery (aucune donnée de la démo fictive)
      journey.set(JOURNEY.lead, { ...(journey.get(JOURNEY.lead) || {}), email: formData.email, name: formData.name, restaurant_name: formData.company || '' });

      // iter-122c — attribution shared_score : dérivée côté serveur (session → compte), rien à déclarer ici.

      toast.success(t('rApp.register.toastSuccess'));

      // If the user landed here from a pricing CTA, send them to Stripe checkout
      // immediately so they can start their 14-day trial. Otherwise, honor a
      // `?next=` query param (e.g., /partners application flow), then dashboard.
      // iter-102 LOT1 (activation): new accounts start with the AI Discovery —
      // first concrete recommendation in <2 minutes, never an empty dashboard.
      const next = searchParams.get('next');
      if (hasCheckoutIntent) {
        const ok = await startStripeCheckout(token);
        if (!ok) navigate('/discovery?welcome=1');
      } else if (next && next.startsWith('/')) {
        navigate(next);
      } else {
        navigate('/discovery?welcome=1');
      }
    } catch (error) {
      toast.error(authErrorMessage(error, t, 'rApp.register.errors.registrationFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => redirectToGoogleAuth(lang);

  // Password strength indicator
  const getPasswordStrength = () => {
    const pwd = formData.password;
    if (!pwd) return { level: 0, text: '' };
    if (pwd.length < 6) return { level: 1, text: t('rApp.register.strength.tooShort') };
    if (pwd.length < 8) return { level: 2, text: t('rApp.register.strength.weak') };
    if (pwd.length >= 8 && /[A-Z]/.test(pwd) && /[0-9]/.test(pwd)) return { level: 4, text: t('rApp.register.strength.strong') };
    return { level: 3, text: t('rApp.register.strength.good') };
  };

  const passwordStrength = getPasswordStrength();

  // Plan label for the confirmation strip (Starter / Growth / Pro)
  // Iter 95 — prix injectés dans la devise régionale (grille 149/279/449)
  const { fmt: rfmt, checkoutCurrency } = useRegion();
  const PLAN_PRICES = { starter: 149, growth: 279, pro: 449 };
  const planConfirmLabel = hasCheckoutIntent
    ? t(`rApp.register.planConfirm.${presetPlan}`)
        .replace('{price}', rfmt(PLAN_PRICES[presetPlan] || 279))
    : null;

  return (
    <div className="premium-auth" data-testid="premium-register">
      {/* Background Effects */}
      <div className="auth-bg">
        <div className="auth-gradient-orb orb-1"></div>
        <div className="auth-gradient-orb orb-2"></div>
        <div className="auth-grid-pattern"></div>
      </div>

      <div className="auth-container">
        {/* Left Side - Branding */}
        <div className="auth-branding">
          <Link to="/" className="auth-logo">
            <div className="logo-icon">
              <img src="/assets/logo/logo-mark-128.png?v=4" alt="Copilote AI Business" style={{width: 28, height: 28, borderRadius: 6}} />
            </div>
            <div className="logo-text">
              <span className="logo-primary">COPILOTE AI</span>
              <span className="logo-secondary">BUSINESS</span>
            </div>
          </Link>

          <div className="branding-content">
            <h1>{t('rApp.register.branding.title')}</h1>
            <p>{t('rApp.register.branding.subtitle')}</p>
            
            <div className="branding-features">
              <div className="feature-item">
                <div className="feature-check">
                  <CheckCircle size={18} />
                </div>
                <div>
                  <strong>{t('rApp.register.branding.feature1Title')}</strong>
                  <span>{t('rApp.register.branding.feature1Body')}</span>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-check">
                  <CheckCircle size={18} />
                </div>
                <div>
                  <strong>{t('rApp.register.branding.feature2Title')}</strong>
                  <span>{t('rApp.register.branding.feature2Body')}</span>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-check">
                  <CheckCircle size={18} />
                </div>
                <div>
                  <strong>{t('rApp.register.branding.feature3Title').replace('{currency}', checkoutCurrency)}</strong>
                  <span>{t('rApp.register.branding.feature3Body')}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="branding-footer">
            <p>© {new Date().getFullYear()} Copilote AI Business</p>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="auth-form-container">
          {/* Mobile-only language switcher so FR/DE/IT visitors who land here
              from a non-localized link can still change language */}
          <div className="auth-lang-mobile" style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 16px 0', position: 'absolute', top: 0, right: 0, zIndex: 5 }} data-testid="register-lang-switcher">
            <LanguageSwitcher />
          </div>

          <div className="auth-form-wrapper">
            {/* Mobile-only Copilote logo — the desktop logo lives in `.auth-branding`
                which is hidden on viewports ≤1024px. This guarantees the brand
                identity is visible on every register-page visit on every device. */}
            <Link to="/" className="auth-logo auth-logo-mobile" data-testid="register-mobile-logo">
              <div className="logo-icon">
                <img src="/assets/logo/logo-mark-128.png?v=4" alt="Copilote AI Business" style={{width: 28, height: 28, borderRadius: 6}} />
              </div>
              <div className="logo-text">
                <span className="logo-primary">COPILOTE AI</span>
                <span className="logo-secondary">BUSINESS</span>
              </div>
            </Link>

            <div className="auth-form-header">
              <h2>{t('rApp.register.headerTitle')}</h2>
              <p>{t('rApp.register.headerSubtitle')}</p>
            </div>

            {/* Plan + Founding confirmation strip — shown only when the visitor
                came from /pricing with ?plan=...&promo=... */}
            {planConfirmLabel && (
              <div
                data-testid="register-plan-confirm"
                style={{
                  display: 'flex', flexDirection: 'column', gap: 6,
                  padding: '12px 14px', borderRadius: 10,
                  border: '1px solid rgba(52,211,153,0.25)',
                  background: 'rgba(52,211,153,0.08)',
                  marginBottom: 16,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: '#A1A1A1', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>
                  <Sparkles size={12} color="#34D399" />
                  <span>{t('rApp.register.planConfirm.label')}</span>
                </div>
                <div style={{ fontSize: 14, color: '#FFFFFF', fontWeight: 600 }}>{planConfirmLabel}</div>
                {isFounding && (
                  <div data-testid="register-founding-applied" style={{ fontSize: 12, color: '#34D399', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <Star size={12} fill="#34D399" /> {t('rApp.register.planConfirm.founding').replace('{fee}', rfmt(299))}
                  </div>
                )}
                <div style={{ fontSize: 12, color: '#C9CFD8', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <Clock size={12} color="#34D399" /> {t('rApp.register.planConfirm.trial')}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="auth-form">
              <div className="form-row">
                <div className="form-group">
                  <Label htmlFor="name">{t('rApp.register.labels.fullName')}</Label>
                  <div className="input-wrapper">
                    <User className="input-icon" size={18} />
                    <Input
                      id="name"
                      type="text"
                      placeholder={t('rApp.register.placeholders.fullName')}
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      className={errors.name ? 'error' : ''}
                      data-testid="register-name"
                    />
                  </div>
                  {errors.name && <span className="error-text">{errors.name}</span>}
                </div>

                <div className="form-group">
                  <Label htmlFor="company">{t('rApp.register.labels.company')}</Label>
                  <div className="input-wrapper">
                    <Building2 className="input-icon" size={18} />
                    <Input
                      id="company"
                      type="text"
                      placeholder={t('rApp.register.placeholders.company')}
                      value={formData.company}
                      onChange={(e) => handleChange('company', e.target.value)}
                      data-testid="register-company"
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <Label htmlFor="email">{t('rApp.register.labels.email')}</Label>
                <div className="input-wrapper">
                  <Mail className="input-icon" size={18} />
                  <Input
                    id="email"
                    type="email"
                    placeholder={t('rApp.register.placeholders.email')}
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className={errors.email ? 'error' : ''}
                    data-testid="register-email"
                  />
                </div>
                {errors.email && <span className="error-text">{errors.email}</span>}
              </div>

              <div className="form-group">
                <Label htmlFor="password">{t('rApp.register.labels.password')}</Label>
                <div className="input-wrapper">
                  <Lock className="input-icon" size={18} />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('rApp.register.placeholders.password')}
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    className={errors.password ? 'error' : ''}
                    data-testid="register-password"
                  />
                  <button 
                    type="button" 
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? t('rApp.login.hidePassword') : t('rApp.login.showPassword')}
                    data-testid="register-password-toggle"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.password && <span className="error-text">{errors.password}</span>}
                
                {formData.password && (
                  <div className="password-strength">
                    <div className="strength-bars">
                      {[1, 2, 3, 4].map(level => (
                        <div 
                          key={level} 
                          className={`bar ${passwordStrength.level >= level ? `level-${passwordStrength.level}` : ''}`} 
                        />
                      ))}
                    </div>
                    <span className={`strength-text level-${passwordStrength.level}`}>
                      {passwordStrength.text}
                    </span>
                  </div>
                )}
              </div>

              <div className="form-terms">
                <label>
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => {
                      setAcceptedTerms(e.target.checked);
                      if (e.target.checked) setErrors((prev) => ({ ...prev, terms: undefined }));
                    }}
                    data-testid="register-terms"
                    aria-required="true"
                    style={{ flexShrink: 0 }}
                  />
                  <span>
                    {tp('rApp.register.terms', {
                      tos: '__TOS__',
                      privacy: '__PRIVACY__',
                    }).split(/(__TOS__|__PRIVACY__)/).map((seg, i) => {
                      if (seg === '__TOS__') return <Link key={i} to="/terms" target="_blank" rel="noopener">{t('rApp.register.termsTos')}</Link>;
                      if (seg === '__PRIVACY__') return <Link key={i} to="/privacy" target="_blank" rel="noopener">{t('rApp.register.termsPrivacy')}</Link>;
                      return seg;
                    })}
                  </span>
                </label>
                {errors.terms && <span className="error-text" data-testid="register-terms-error">{errors.terms}</span>}
                <label style={{ marginTop: 8 }}>
                  <input
                    type="checkbox"
                    checked={marketingOptIn}
                    onChange={(e) => setMarketingOptIn(e.target.checked)}
                    data-testid="register-marketing-optin"
                    style={{ flexShrink: 0 }}
                  />
                  <span>{t('rApp.register.marketingOptIn')}</span>
                </label>
              </div>

              <Button 
                type="submit" 
                className="submit-btn"
                disabled={loading}
                data-testid="register-submit"
              >
                {loading ? t('rApp.register.submitLoading') : t('rApp.register.submit')}
                <ArrowRight size={18} />
              </Button>
            </form>

            <div className="auth-divider">
              <span>{t('rApp.register.divider')}</span>
            </div>

            <button 
              type="button" 
              className="google-btn"
              onClick={handleGoogleLogin}
              data-testid="google-register"
            >
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t('rApp.register.googleCta')}
            </button>
            <p style={{ fontSize: 11.5, color: '#8A8A8A', textAlign: 'center', marginTop: 8, lineHeight: 1.5 }} data-testid="google-terms-note">
              {t('rApp.register.googleTermsNote')}
            </p>

            <p className="auth-footer-text">
              {t('rApp.register.alreadyHave')} <Link to="/login">{t('rApp.register.signIn')}</Link>
            </p>

            <div style={{ marginTop: 12, borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 6 }}>
              <TrustBar variant="compact" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PremiumRegister;
